import { getCachedScore } from "./scores";
import { invalidateTransactionsSnapshot } from "./wheelTransactions";

const FETCH_TIMEOUT_MS = 25_000;

function withFetchTimeout<T>(promise: Promise<T>, label: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      setTimeout(
        () => reject(new Error(`${label} timed out after ${Math.round(FETCH_TIMEOUT_MS / 1000)}s`)),
        FETCH_TIMEOUT_MS,
      );
    }),
  ]);
}

export const WATCHLIST_STORAGE_KEY = "sfe-focus-mode-tickers";
export const WATCHLIST_COLLAPSED_STORAGE_KEY = "sfe-watchlist-collapsed-tickers";

const WHEEL_API_BASE = "/api/v1/wheel";
const TICKER_CACHE_MS = 24 * 60 * 60 * 1000;
const POSITIONS_CACHE_MS = 60_000;
const RECOMMENDATIONS_CACHE_MS = 60_000;
const REPORT_CACHE_MS = 5 * 60 * 1000;

export interface WheelTickerEntry {
  s: string;
  n: string;
}

export interface OptionPosition {
  option_symbol: string;
  ticker: string;
  /** Robinhood option instrument UUID when known. */
  option_id?: string;
  /** Robinhood option chain UUID when known (app option-chain deep link). */
  chain_id?: string;
  sector?: string;
  target_price?: number;
  strike: number;
  side: string;
  exp_date: string;
  open_date?: string;
  open_credit?: number;
  contracts: number;
  entry_price?: number;
  current_price?: number;
  stock_price?: number;
  current_profit?: number;
  current_profit_pct?: number;
  /** Robinhood chance-of-profit when positions come from RH quotes. */
  pop?: number;
  collateral?: number;
  dte?: number;
  expired?: boolean;
  /** Own-ledger account that holds the remaining short lots. */
  account_id?: string;
  /** Distinct accounts still open on this contract. */
  account_count?: number;
  broker_id?: string;
  broker_name?: string;
  account_label?: string;
  account_short_name?: string;
}

export interface StockPosition {
  ticker: string;
  sector?: string;
  target_price?: number;
  quantity: number;
  avg_price?: number;
  cost_basis?: number;
  current_price?: number;
  current_pl?: number;
  current_pl_pct?: number;
  days_held?: number;
}

export interface TickerReport {
  ticker: string;
  decision?: string;
  normalized_score?: number;
  company?: string;
  sector?: string;
  industry?: string;
  price?: number;
  target_price?: number;
  pe?: number;
  market_cap?: number;
}

export interface WheelRecommendation {
  underlying_ticker: string;
  side: string;
  strike: number;
  expiration_date: string;
  dte?: number;
  mid?: number;
  last_price?: number;
  bid?: number;
  ask?: number;
  roi?: number;
  pop?: number;
  collateral?: number;
  decision?: string;
  underlying_price?: number;
  target_price?: number;
  holding_avg_cost?: number;
  option_symbol?: string;
  next_earnings_date?: string | null;
  expires_after_earnings?: boolean;
  ready_call_contracts?: number;
  available_call_contracts?: number;
  opened_calls?: number;
  /** Finviz sector on screener rows (when provided by API). */
  sector?: string;
  /** Market cap in millions (Finviz). */
  market_cap?: number;
  /** Index membership codes: sp500, ndx, dji, rut. */
  index_badges?: string[];
  open_put_contracts?: number;
  /** Long stock in 100-share blocks (native held column). */
  stock_blocks?: number;
  covered_call_contracts?: number;
  rec_quantity?: number;
  report_state?: string;
  is_golden?: boolean;
  /** Long shares held (from positions snapshot). */
  sfe_shares_held?: number | null;
  /** Shares not under open short call contracts. */
  sfe_shares_available?: number | null;
  /** Injected advisor contract that is not in the current screener snapshot. */
  sfe_advice?: boolean;
}

/** Covered-call contracts still available to sell (native screener semantics). */
export function callContractsAvailable(
  rec: Pick<
    WheelRecommendation,
    "ready_call_contracts" | "available_call_contracts" | "opened_calls"
  >,
): number {
  if (rec.ready_call_contracts != null && Number.isFinite(rec.ready_call_contracts)) {
    return Math.max(0, rec.ready_call_contracts);
  }
  const cap = Number(rec.available_call_contracts) || 0;
  const open = Number(rec.opened_calls) || 0;
  return Math.max(0, cap - open);
}

export interface PositionsSnapshot {
  options: OptionPosition[];
  stock: StockPosition[];
  fetchedAt: number;
}

export interface RecommendationsSnapshot {
  puts: WheelRecommendation[];
  calls: WheelRecommendation[];
  fetchedAt: number;
}

let tickerUniverse: WheelTickerEntry[] = [];
let tickerUniverseAt = 0;
let positionsSnapshot: PositionsSnapshot | null = null;
let recommendationsSnapshot: RecommendationsSnapshot | null = null;
let recommendationsInflight: Promise<RecommendationsSnapshot> | null = null;
let positionsInflight: Promise<PositionsSnapshot> | null = null;
const reportCache = new Map<string, { report: TickerReport | null; at: number }>();

/** Drop in-memory wheel snapshots (e.g. before an explicit refresh). */
export function invalidateWheelSnapshots(): void {
  recommendationsSnapshot = null;
  positionsSnapshot = null;
  recommendationsInflight = null;
  positionsInflight = null;
  invalidateTransactionsSnapshot();
}

export function loadSavedTickers(): string[] {
  try {
    const raw = localStorage.getItem(WATCHLIST_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return [...new Set(parsed.map(normalizeTicker).filter(Boolean))];
  } catch {
    return [];
  }
}

export function saveTickers(tickers: string[]): void {
  localStorage.setItem(WATCHLIST_STORAGE_KEY, JSON.stringify(tickers));
}

export function loadCollapsedTickers(): Set<string> {
  try {
    const raw = localStorage.getItem(WATCHLIST_COLLAPSED_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.map((t) => normalizeTicker(String(t))).filter(Boolean));
  } catch {
    return new Set();
  }
}

export function saveCollapsedTickers(collapsed: Set<string>): void {
  try {
    localStorage.setItem(WATCHLIST_COLLAPSED_STORAGE_KEY, JSON.stringify([...collapsed]));
  } catch {
    /* ignore quota / private mode */
  }
}

export function normalizeTicker(raw: string): string {
  return String(raw || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9._-]/g, "")
    .slice(0, 16);
}

export function esc(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function fmtUsd(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const sign = value < 0 ? "-" : "";
  return `${sign}$${Math.abs(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function fmtUsdWhole(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const sign = value < 0 ? "-" : "";
  return `${sign}$${Math.round(Math.abs(value)).toLocaleString("en-US", {
    maximumFractionDigits: 0,
  })}`;
}

export function fmtNum(value: number | null | undefined, digits = 0): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return value.toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function fmtPct(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const pct = Math.abs(value) <= 1 ? value * 100 : value;
  const sign = pct < 0 ? "-" : "";
  return `${sign}${Math.abs(pct).toFixed(1)}%`;
}

export function fmtWheelRatio(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${(value * 100).toFixed(1)}%`;
}

export function plClass(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "";
  return value >= 0 ? "roi-strong" : "roi-loss";
}

export type CallStrikeVsAvg = "below" | "equal" | "above";

export function callStrikeVsAvgCost(
  strike: number | null | undefined,
  avgPrice: number | null | undefined,
): CallStrikeVsAvg | null {
  if (
    strike == null ||
    avgPrice == null ||
    !Number.isFinite(strike) ||
    !Number.isFinite(avgPrice)
  ) {
    return null;
  }
  const diff = strike - avgPrice;
  if (Math.abs(diff) < 0.005) return "equal";
  if (diff < 0) return "below";
  return "above";
}

export function callStrikeVsAvgValueClass(vs: CallStrikeVsAvg | null): string {
  switch (vs) {
    case "below":
      return "roi-loss";
    case "above":
      return "roi-strong";
    case "equal":
      return "sfe-call-strike-at-avg";
    default:
      return "";
  }
}

/** Covered-call assignment risk (below avg) or upside opportunity (above avg), in dollars. */
export function callRiskOptyExposure(
  side: string | null | undefined,
  strike: number | null | undefined,
  avgCost: number | null | undefined,
  contracts: number | null | undefined,
): { kind: "risk" | "opty"; amount: number } | null {
  const isCall = String(side || "")
    .trim()
    .toLowerCase()
    .startsWith("c");
  if (!isCall) return null;
  const vs = callStrikeVsAvgCost(strike, avgCost);
  if (vs !== "below" && vs !== "above") return null;
  const qty = Math.abs(Number(contracts) || 0) * 100;
  if (!Number.isFinite(qty) || qty <= 0) return null;
  const amount = Math.abs((strike as number) - (avgCost as number)) * qty;
  if (!Number.isFinite(amount) || amount <= 0) return null;
  return { kind: vs === "below" ? "risk" : "opty", amount };
}

export function callRiskOptyValueClass(kind: "risk" | "opty" | null | undefined): string {
  if (kind === "risk") return "roi-loss";
  if (kind === "opty") return "roi-strong";
  return "";
}

export function callStrikeVsAvgTip(
  strike: number | null | undefined,
  avgPrice: number | null | undefined,
  shares?: number | null,
): string | undefined {
  const vs = callStrikeVsAvgCost(strike, avgPrice);
  if (vs !== "below" && vs !== "above") return undefined;
  const diff = (strike as number) - (avgPrice as number);
  const sign = diff >= 0 ? "+" : "-";
  const delta = `${sign}${Math.abs(diff).toFixed(2)}`;
  const qty = Number(shares);
  if (vs === "below") {
    let tip = `Below avg cost (${delta})`;
    if (Number.isFinite(qty) && qty > 0) {
      const atRisk = Math.max(0, (avgPrice as number) - (strike as number)) * qty;
      tip += `\nAt risk: ${fmtUsd(atRisk)}`;
    }
    return tip;
  }
  let tip = `Above avg cost (${delta})`;
  if (Number.isFinite(qty) && qty > 0) {
    const opty = Math.max(0, (strike as number) - (avgPrice as number)) * qty;
    tip += `\nOpportunity: ${fmtUsd(opty)}`;
  }
  return tip;
}

export function renderCallStrikeTipPopover(
  strike: number | null | undefined,
  avgPrice: number | null | undefined,
  shares?: number | null,
): string {
  const vs = callStrikeVsAvgCost(strike, avgPrice);
  if (vs !== "below" && vs !== "above") return "";
  const diff = (strike as number) - (avgPrice as number);
  const sign = diff >= 0 ? "+" : "-";
  const delta = `${sign}${Math.abs(diff).toFixed(2)}`;
  if (vs === "below") {
    const qty = Number(shares);
    const atRisk =
      Number.isFinite(qty) && qty > 0
        ? Math.max(0, (avgPrice as number) - (strike as number)) * qty
        : null;
    if (atRisk != null) {
      return `<span class="sfe-data-tip-popover" role="tooltip"><span class="sfe-data-tip-line">Below avg cost (${delta})</span><span class="sfe-data-tip-line">At risk: <span class="sfe-data-tip-loss">${fmtUsd(atRisk)}</span></span></span>`;
    }
    return `<span class="sfe-data-tip-popover" role="tooltip"><span class="sfe-data-tip-line">Below avg cost (${delta})</span></span>`;
  }
  const qty = Number(shares);
  const opty =
    Number.isFinite(qty) && qty > 0
      ? Math.max(0, (strike as number) - (avgPrice as number)) * qty
      : null;
  if (opty != null) {
    return `<span class="sfe-data-tip-popover" role="tooltip"><span class="sfe-data-tip-line">Above avg cost (${delta})</span><span class="sfe-data-tip-line">Opportunity: <span class="sfe-data-tip-gain">${fmtUsd(opty)}</span></span></span>`;
  }
  return `<span class="sfe-data-tip-popover" role="tooltip"><span class="sfe-data-tip-line">Above avg cost (${delta})</span></span>`;
}

export function callStrikeTipHostClass(
  strike: number | null | undefined,
  avgPrice: number | null | undefined,
): string {
  return callStrikeVsAvgTip(strike, avgPrice) ? "sfe-data-tip-host" : "";
}

export function sideLabel(side: string): string {
  return side === "call" ? "Call" : side === "put" ? "Put" : side;
}

/**
 * Proximity gate for ATM badge on OTM legs.
 * Market convention: ATM is the strike closest to spot; "near the money" is often ~0.5% or $0.50.
 * We use max(0.5% of strike, $0.50) so low-priced names still get a sensible absolute floor.
 */
export const OPTION_ATM_BAND_PCT = 0.005;
export const OPTION_ATM_MIN_ABS_USD = 0.5;

export type OptionMoneyness = "itm" | "atm" | null;

function resolveOptionStockPrice(
  position: OptionPosition,
  stockPrice?: number | null,
): number | null {
  const stock = stockPrice ?? position.stock_price;
  return stock != null && Number.isFinite(stock) ? stock : null;
}

export function optionIsItm(position: OptionPosition, stockPrice?: number | null): boolean {
  const stock = resolveOptionStockPrice(position, stockPrice);
  const strike = position.strike;
  if (stock == null || strike == null || !Number.isFinite(strike)) {
    return false;
  }
  if (position.side === "call") return stock > strike;
  if (position.side === "put") return stock < strike;
  return false;
}

export function optionDistanceToAtm(stock: number, strike: number): number {
  return Math.abs(stock - strike);
}

/** True when spot is within the ATM proximity band of strike (relative + absolute floor). */
export function isWithinAtmProximity(stock: number, strike: number): boolean {
  if (!Number.isFinite(stock) || !Number.isFinite(strike) || strike <= 0) return false;
  const dist = optionDistanceToAtm(stock, strike);
  return dist <= Math.max(OPTION_ATM_MIN_ABS_USD, strike * OPTION_ATM_BAND_PCT);
}

function atmBadgeGroupKey(position: OptionPosition): string {
  const side = String(position.side ?? "")
    .trim()
    .toLowerCase();
  const exp = String(position.expiration_date ?? "").trim();
  return `${normalizeTicker(position.ticker)}|${side}|${exp}`;
}

/** ITM when in the money. ATM is assigned in batch — see assignAtmBadges. */
export function optionMoneyness(
  position: OptionPosition,
  stockPrice?: number | null,
): OptionMoneyness {
  const stock = resolveOptionStockPrice(position, stockPrice);
  const strike = position.strike;
  if (stock == null || strike == null || !Number.isFinite(strike) || strike <= 0) {
    return null;
  }
  if (optionIsItm(position, stock)) return "itm";
  return null;
}

/**
 * Mark ATM on OTM legs: per ticker + side + expiry, only the strike nearest spot
 * gets the badge, and only if within the ATM proximity band (closest-strike convention).
 */
export function assignAtmBadges<
  T extends { position: OptionPosition; stockPrice: number | null; moneyness: OptionMoneyness },
>(rows: T[]): void {
  const groups = new Map<string, T[]>();
  for (const row of rows) {
    if (row.moneyness === "itm" || row.stockPrice == null) continue;
    const strike = row.position.strike;
    if (strike == null || !Number.isFinite(strike) || strike <= 0) continue;
    if (optionIsItm(row.position, row.stockPrice)) continue;
    const key = atmBadgeGroupKey(row.position);
    const bucket = groups.get(key) ?? [];
    bucket.push(row);
    groups.set(key, bucket);
  }

  for (const bucket of groups.values()) {
    let best: T | null = null;
    let bestDist = Infinity;
    for (const row of bucket) {
      const dist = optionDistanceToAtm(row.stockPrice!, row.position.strike!);
      if (dist < bestDist) {
        bestDist = dist;
        best = row;
      }
    }
    if (best && isWithinAtmProximity(best.stockPrice!, best.position.strike!)) {
      best.moneyness = "atm";
    }
  }
}

export function sharesUnderCallContract(options: OptionPosition[]): number {
  return options
    .filter((p) => !p.expired && p.side === "call")
    .reduce((sum, p) => sum + Math.abs(p.contracts ?? 0) * 100, 0);
}

export function sharesAvailable(
  stock: StockPosition | undefined,
  options: OptionPosition[],
): number | null {
  if (!stock || stock.quantity == null || !Number.isFinite(stock.quantity)) return null;
  return Math.max(0, stock.quantity - sharesUnderCallContract(options));
}

export function sharesForTicker(
  snapshot: PositionsSnapshot,
  ticker: string,
): { held: number | null; available: number | null } {
  const key = normalizeTicker(ticker);
  const stock = snapshot.stock.find((row) => normalizeTicker(row.ticker) === key);
  if (!stock || stock.quantity == null || !Number.isFinite(stock.quantity)) {
    return { held: null, available: null };
  }
  const held = Math.abs(stock.quantity);
  const options = snapshot.options.filter((row) => normalizeTicker(row.ticker) === key);
  return { held, available: sharesAvailable(stock, options) };
}

export function optionPremiumReceived(position: OptionPosition): number | null {
  if (position.open_credit != null && Number.isFinite(position.open_credit)) {
    return Math.abs(position.open_credit);
  }
  if (
    position.entry_price != null &&
    position.contracts != null &&
    Number.isFinite(position.entry_price) &&
    Number.isFinite(position.contracts)
  ) {
    return position.entry_price * Math.abs(position.contracts) * 100;
  }
  return null;
}

/** Assignment collateral: strike × 100 × |contracts|. */
export function optionStrikeCollateral(
  position: Pick<OptionPosition, "strike" | "contracts">,
): number | null {
  const strike = Number(position.strike);
  const contracts = Math.abs(Number(position.contracts) || 0);
  if (!Number.isFinite(strike) || strike <= 0 || contracts <= 0) return null;
  return strike * contracts * 100;
}

/** Capital the Open/Held ROC columns use: cash strike for puts, share avg cost for calls. */
export function optionRocCollateral(
  position: Pick<OptionPosition, "strike" | "contracts" | "side">,
  avgCost?: number | null,
): number | null {
  const contracts = Math.abs(Number(position.contracts) || 0);
  if (contracts <= 0) return null;
  if (/^(call|c)$/i.test(position.side ?? "")) {
    const cost = Number(avgCost);
    if (!Number.isFinite(cost) || cost <= 0) return null;
    return cost * contracts * 100;
  }
  return optionStrikeCollateral(position);
}

export function roiClass(roi: number | null | undefined): string {
  if (roi == null || !Number.isFinite(roi)) return "";
  if (roi >= 0.25) return "roi-strong";
  if (roi >= 0.12) return "roi-mid";
  return "";
}

export function wheelRubricClass(decision: string): string {
  const t = decision.toLowerCase();
  if (/no\s*report|missing|unavailable/.test(t)) return "wheel-rubric-missing";
  if (/avoid|weak|flagged/.test(t)) return "wheel-rubric-avoid";
  if (/small|lower/.test(t)) return "wheel-rubric-small";
  if (/selective|moderate/.test(t)) return "wheel-rubric-selective";
  if (/higher|good/.test(t)) return "wheel-rubric-good";
  return "wheel-rubric-insufficient";
}

export function scoreForTicker(ticker: string, report: TickerReport | null): string {
  const score = report?.normalized_score ?? getCachedScore(ticker);
  if (typeof score === "number") return `${Math.round(score)}/100`;
  if (score === null) return "—";
  return "…";
}

export function resolvePriceTarget(
  report: TickerReport | null,
  stock: StockPosition | undefined,
  puts: WheelRecommendation[],
  calls: WheelRecommendation[],
): { price: number | undefined; target: number | undefined } {
  const price =
    report?.price ??
    stock?.current_price ??
    puts[0]?.underlying_price ??
    calls[0]?.underlying_price;
  const target =
    report?.target_price ??
    stock?.target_price ??
    puts[0]?.target_price ??
    calls[0]?.target_price;
  return { price, target };
}

export function sectorForTicker(
  report: TickerReport | null,
  stock: StockPosition | undefined,
  options: OptionPosition[],
): string | undefined {
  return report?.sector ?? stock?.sector ?? options[0]?.sector;
}

async function wheelFetch<T = unknown>(path: string, force = false): Promise<T> {
  const fullPath = `${WHEEL_API_BASE}${path}`;

  const pageApi = (window as unknown as { API?: { get: (p: string, o?: object) => Promise<T> } }).API;
  if (pageApi?.get) {
    return withFetchTimeout(pageApi.get(fullPath, { silent: true }), fullPath);
  }

  const headers: Record<string, string> = { Accept: "application/json" };
  if (force) {
    headers["Cache-Control"] = "no-cache";
    headers["X-SFE-Refresh"] = "1";
  }

  const res = await withFetchTimeout(
    fetch(fullPath, {
      credentials: "include",
      headers,
    }),
    fullPath,
  );
  if (res.ok) return res.json() as Promise<T>;

  throw new Error(`${path}: fetch failed`);
}

export function shareAvgFromStock(stock: StockPosition): number | undefined {
  if (typeof stock.avg_price === "number" && Number.isFinite(stock.avg_price)) {
    return stock.avg_price;
  }
  const qty = Number(stock.quantity);
  const basis = Number(stock.cost_basis);
  if (qty > 0 && Number.isFinite(basis)) return basis / qty;
  return undefined;
}

/** Strike-based share avg — own ledger first (matches Stocks tab), then SF rec/stock. */
export function resolveShareAvgCostForTicker(
  ticker: string,
  stockAvgs: Map<string, number>,
  rec?: Pick<WheelRecommendation, "holding_avg_cost">,
  stock?: StockPosition,
  accountId?: string | null,
): number | undefined {
  const key = normalizeTicker(ticker);
  const account = String(accountId ?? "").trim();
  if (account) {
    const exact = stockAvgs.get(`${account}|${key}`);
    if (exact != null && Number.isFinite(exact) && exact > 0) return exact;
  }
  const own = stockAvgs.get(key);
  if (own != null && Number.isFinite(own) && own > 0) return own;
  if (rec?.holding_avg_cost != null && Number.isFinite(rec.holding_avg_cost)) {
    return rec.holding_avg_cost;
  }
  if (stock) {
    const fromStock = shareAvgFromStock(stock);
    if (fromStock != null) return fromStock;
  }
  return undefined;
}

function fillShareAvgFromStockTable(map: Map<string, number>): void {
  const table = document.getElementById("wheelStockTable");
  if (!(table instanceof HTMLTableElement)) return;
  const headerRow = table.querySelector("thead tr");
  if (!(headerRow instanceof HTMLTableRowElement)) return;

  let tickerIdx = -1;
  let avgIdx = -1;
  Array.from(headerRow.children).forEach((th, i) => {
    const col = th.getAttribute("data-col");
    const sort = th.getAttribute("data-sort");
    if (col === "ticker" || sort === "ticker") tickerIdx = i;
    // Strike-based avg only — never read adjusted_avg for options context.
    if (col === "avg_price" || sort === "avg_price") avgIdx = i;
  });
  if (tickerIdx < 0 || avgIdx < 0) return;

  for (const tr of table.querySelectorAll("tbody tr")) {
    if (!(tr instanceof HTMLTableRowElement)) continue;
    const cells = tr.querySelectorAll("td, th");
    const tickerCell = cells[tickerIdx];
    const avgCell = cells[avgIdx];
    if (!tickerCell || !avgCell) continue;

    const text = (tickerCell.textContent ?? "").trim();
    const match = text.match(/^([A-Z]{1,6})\b/);
    const key = normalizeTicker(match ? match[1] : text);
    if (!key || map.has(key)) continue;

    const avgText = (avgCell.textContent ?? "").replace(/[$,]/g, "").trim();
    const avg = Number.parseFloat(avgText);
    if (Number.isFinite(avg)) map.set(key, avg);
  }
}

/** Share average cost by ticker — own-ledger strike basis first, then SF+/recs. */
export async function buildShareAvgByTicker(force = false): Promise<Map<string, number>> {
  const map = new Map<string, number>();

  // Prefer own-ledger strike-based avg (assignment lots use strike, not premium-adjusted tax lot).
  try {
    const { loadOwnLedgerStrikeAvgMap } = await import("./ownLedgerClient");
    const own = await loadOwnLedgerStrikeAvgMap(force);
    for (const [ticker, avg] of own) {
      if (avg > 0) map.set(ticker, avg);
    }
  } catch {
    /* optional */
  }

  try {
    const snap = await loadPositionsSnapshot(force);
    for (const stock of snap.stock) {
      const key = normalizeTicker(stock.ticker);
      if (map.has(key)) continue;
      const avg = shareAvgFromStock(stock);
      if (avg != null) map.set(key, avg);
    }
  } catch {
    /* fall through to other sources */
  }

  if (!map.size) {
    try {
      const data = await wheelFetch<{ positions?: StockPosition[] }>("/stock-positions");
      for (const stock of data?.positions ?? []) {
        const key = normalizeTicker(stock.ticker);
        if (map.has(key)) continue;
        const avg = shareAvgFromStock(stock);
        if (avg != null) map.set(key, avg);
      }
    } catch {
      /* optional */
    }
  }

  try {
    const recs = await loadRecommendationsSnapshot(force);
    for (const rec of recs.calls) {
      const key = normalizeTicker(rec.underlying_ticker);
      if (map.has(key)) continue;
      if (typeof rec.holding_avg_cost === "number" && Number.isFinite(rec.holding_avg_cost)) {
        map.set(key, rec.holding_avg_cost);
      }
    }
  } catch {
    /* optional */
  }

  // DOM stock table fills gaps only (fillShareAvgFromStockTable skips existing keys).
  fillShareAvgFromStockTable(map);
  return map;
}

async function apiFetch<T = unknown>(path: string): Promise<T | null> {
  try {
    const pageApi = (window as unknown as { API?: { get: (p: string, o?: object) => Promise<T> } }).API;
    if (pageApi?.get) {
      return pageApi.get(path, { silent: true });
    }
    const res = await fetch(path, {
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

export async function ensureTickerUniverse(): Promise<WheelTickerEntry[]> {
  if (tickerUniverse.length && Date.now() - tickerUniverseAt < TICKER_CACHE_MS) {
    return tickerUniverse;
  }
  const data = await wheelFetch<{ tickers?: WheelTickerEntry[] }>("/tickers");
  tickerUniverse = Array.isArray(data.tickers) ? data.tickers : [];
  tickerUniverseAt = Date.now();
  return tickerUniverse;
}

export function searchTickers(query: string, limit = 12): WheelTickerEntry[] {
  const q = normalizeTicker(query);
  if (!q) return [];
  const upper = q.toUpperCase();
  const starts: WheelTickerEntry[] = [];
  const contains: WheelTickerEntry[] = [];

  for (const entry of tickerUniverse) {
    const sym = entry.s.toUpperCase();
    if (sym.startsWith(upper)) starts.push(entry);
    else if (sym.includes(upper)) contains.push(entry);
    if (starts.length >= limit) break;
  }

  return [...starts, ...contains].slice(0, limit);
}

export function peekRecommendationsSnapshot(): RecommendationsSnapshot | null {
  return recommendationsSnapshot;
}

export function peekPositionsSnapshot(): PositionsSnapshot | null {
  return positionsSnapshot;
}

export async function loadPositionsSnapshot(force = false): Promise<PositionsSnapshot> {
  if (
    !force &&
    positionsSnapshot &&
    Date.now() - positionsSnapshot.fetchedAt < POSITIONS_CACHE_MS
  ) {
    return positionsSnapshot;
  }

  if (positionsInflight && !force) {
    return positionsInflight;
  }

  const fetchPositions = async (): Promise<PositionsSnapshot> => {
    try {
      const [optData, stockData] = await Promise.all([
        wheelFetch<{ positions?: OptionPosition[] }>("/positions", force),
        wheelFetch<{ positions?: StockPosition[] }>("/stock-positions", force),
      ]);

      positionsSnapshot = {
        options: Array.isArray(optData.positions) ? optData.positions : [],
        stock: Array.isArray(stockData.positions) ? stockData.positions : [],
        fetchedAt: Date.now(),
      };
      return positionsSnapshot;
    } catch (err) {
      if (positionsSnapshot) return positionsSnapshot;
      throw err;
    } finally {
      positionsInflight = null;
    }
  };

  positionsInflight = fetchPositions();
  return positionsInflight;
}

export function stockForTicker(ticker: string): StockPosition | undefined {
  if (!positionsSnapshot) return undefined;
  const key = normalizeTicker(ticker);
  return positionsSnapshot.stock.find((p) => normalizeTicker(p.ticker) === key);
}

export async function loadRecommendationsSnapshot(force = false): Promise<RecommendationsSnapshot> {
  if (
    !force &&
    recommendationsSnapshot &&
    Date.now() - recommendationsSnapshot.fetchedAt < RECOMMENDATIONS_CACHE_MS
  ) {
    return recommendationsSnapshot;
  }

  if (recommendationsInflight && !force) {
    return recommendationsInflight;
  }

  const fetchRecommendations = async (): Promise<RecommendationsSnapshot> => {
    try {
      const [putData, callData] = await Promise.all([
        wheelFetch<{ recommendations?: WheelRecommendation[] }>("/recommendations/puts", force),
        wheelFetch<{ recommendations?: WheelRecommendation[] }>("/recommendations/calls", force),
      ]);

      recommendationsSnapshot = {
        puts: Array.isArray(putData.recommendations) ? putData.recommendations : [],
        calls: Array.isArray(callData.recommendations) ? callData.recommendations : [],
        fetchedAt: Date.now(),
      };
      return recommendationsSnapshot;
    } catch (err) {
      if (recommendationsSnapshot) return recommendationsSnapshot;
      throw err;
    } finally {
      recommendationsInflight = null;
    }
  };

  recommendationsInflight = fetchRecommendations();
  return recommendationsInflight;
}

export function recommendationsForTicker(
  snapshot: RecommendationsSnapshot,
  ticker: string,
): { puts: WheelRecommendation[]; calls: WheelRecommendation[] } {
  const key = normalizeTicker(ticker);
  return {
    puts: snapshot.puts.filter((r) => normalizeTicker(r.underlying_ticker) === key),
    calls: snapshot.calls.filter((r) => normalizeTicker(r.underlying_ticker) === key),
  };
}

export function normalizeRecDateKey(value: string | null | undefined): string {
  const s = String(value ?? "").trim();
  if (!s) return "";
  const iso = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (iso) return iso[1];
  const parsed = Date.parse(s);
  if (!Number.isNaN(parsed)) {
    const d = new Date(parsed);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }
  return s.toUpperCase();
}

export function recommendationLookupKey(
  ticker: string,
  strike: number,
  expiration: string,
): string {
  return `${normalizeTicker(ticker)}|${strike}|${normalizeRecDateKey(expiration)}`;
}

export function buildRecommendationLookup(
  snapshot: RecommendationsSnapshot,
): Map<string, WheelRecommendation> {
  const map = new Map<string, WheelRecommendation>();
  for (const rec of [...snapshot.puts, ...snapshot.calls]) {
    const ticker = normalizeTicker(rec.underlying_ticker);
    if (!ticker) continue;
    if (Number.isFinite(rec.strike) && rec.expiration_date) {
      map.set(recommendationLookupKey(ticker, rec.strike, rec.expiration_date), rec);
    }
    if (rec.option_symbol) map.set(`sym:${rec.option_symbol}`, rec);
  }
  return map;
}

export function matchPositionToRecommendation(
  position: OptionPosition,
  lookup: Map<string, WheelRecommendation>,
): WheelRecommendation | undefined {
  if (position.option_symbol) {
    const bySymbol = lookup.get(`sym:${position.option_symbol}`);
    if (bySymbol) return bySymbol;
  }
  const ticker = normalizeTicker(position.ticker);
  if (ticker && Number.isFinite(position.strike) && position.exp_date) {
    return lookup.get(recommendationLookupKey(ticker, position.strike, position.exp_date));
  }
  return undefined;
}

export async function loadTickerReport(ticker: string): Promise<TickerReport | null> {
  const key = normalizeTicker(ticker);
  const cached = reportCache.get(key);
  if (cached && Date.now() - cached.at < REPORT_CACHE_MS) return cached.report;

  const data = await apiFetch<{
    ticker?: string;
    decision?: string;
    normalized_score?: number;
    data?: { finviz?: { normalized?: Record<string, unknown> } };
  }>(`/v1/reports/${encodeURIComponent(key)}/latest`);

  if (!data) {
    reportCache.set(key, { report: null, at: Date.now() });
    return null;
  }

  const finviz = data.data?.finviz?.normalized ?? {};
  const report: TickerReport = {
    ticker: data.ticker ?? key,
    decision: data.decision,
    normalized_score:
      typeof data.normalized_score === "number" ? data.normalized_score : undefined,
    company: typeof finviz.company === "string" ? finviz.company : undefined,
    sector: typeof finviz.sector === "string" ? finviz.sector : undefined,
    industry: typeof finviz.industry === "string" ? finviz.industry : undefined,
    price: typeof finviz.price === "number" ? finviz.price : undefined,
    target_price: typeof finviz.target_price === "number" ? finviz.target_price : undefined,
    pe: typeof finviz.pe === "number" ? finviz.pe : undefined,
    market_cap: typeof finviz.market_cap === "number" ? finviz.market_cap : undefined,
  };

  reportCache.set(key, { report, at: Date.now() });
  return report;
}

export function summarizeOptions(rows: OptionPosition[]): string {
  if (!rows.length) return "—";
  const open = rows.filter((r) => !r.expired);
  const list = open.length ? open : rows;
  if (list.length === 1) {
    const p = list[0];
    const pl = fmtUsd(p.current_profit);
    const dte = p.dte != null ? `${p.dte}d` : "—";
    return `${list.length} ${sideLabel(p.side).toLowerCase()} · $${fmtNum(p.strike, 2)} × ${fmtNum(p.contracts, 0)} · ${pl} · ${dte}`;
  }
  const totalPl = list.reduce((sum, p) => sum + (p.current_profit ?? 0), 0);
  return `${list.length} options · ${fmtUsd(totalPl)} total P/L`;
}

export function summarizeStock(row: StockPosition | undefined): string {
  if (!row) return "—";
  const pl = fmtUsd(row.current_pl);
  return `${fmtNum(row.quantity, 0)} sh · ${fmtUsd(row.avg_price)} avg · ${pl}`;
}

export function fmtEarningsDate(value: string | null | undefined): string {
  if (!value) return "—";
  const trimmed = value.trim();
  if (!trimmed) return "—";
  const iso = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
  return iso ? iso[1] : trimmed;
}

/** Calendar days from today (local midnight) to an ISO date string. */
export function daysUntilCalendarDate(value: string | null | undefined): number | null {
  if (!value) return null;
  const iso = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!iso) return null;
  const target = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  const diff = Math.round((target.getTime() - today.getTime()) / 86_400_000);
  return Number.isFinite(diff) ? diff : null;
}

export function fmtEarningsInDays(value: string | null | undefined): string {
  const days = daysUntilCalendarDate(value);
  if (days == null) return "—";
  if (days === 0) return "today";
  if (days === 1) return "in 1 day";
  if (days > 1) return `in ${days} days`;
  if (days === -1) return "1 day ago";
  return `${Math.abs(days)} days ago`;
}

export function earningsWithinExpiry(
  rec: Pick<WheelRecommendation, "expires_after_earnings"> | undefined,
): boolean {
  return rec?.expires_after_earnings === true;
}

export function earningsDateTip(rec: Pick<WheelRecommendation, "expires_after_earnings"> | undefined): string {
  if (rec?.expires_after_earnings === true) {
    return "Next earnings before option expiration.";
  }
  if (rec?.expires_after_earnings === false) {
    return "Option expires before next earnings.";
  }
  return "Next earnings date.";
}

export function summarizeRec(rec: WheelRecommendation | undefined): string {
  if (!rec) return "—";
  const dte = rec.dte != null ? `${rec.dte}d` : "—";
  const earn = fmtEarningsDate(rec.next_earnings_date);
  const earnPart = earn !== "—" ? ` · Earn ${earn}` : "";
  return `$${fmtNum(rec.strike, 2)} · ${dte}${earnPart} · ${fmtWheelRatio(rec.roi)} ROI · ${fmtWheelRatio(rec.pop)} POP`;
}

export function suggestedFromScreener(
  watchlist: string[],
  recommendations: RecommendationsSnapshot,
  limit = 6,
): WheelRecommendation[] {
  const watched = new Set(watchlist.map(normalizeTicker));
  const seen = new Set<string>();
  const out: WheelRecommendation[] = [];

  for (const rec of recommendations.puts) {
    const key = normalizeTicker(rec.underlying_ticker);
    if (watched.has(key) || seen.has(key)) continue;
    seen.add(key);
    out.push(rec);
    if (out.length >= limit) break;
  }
  return out;
}
